import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import EastIcon from '@mui/icons-material/East';
import WestIcon from '@mui/icons-material/West';
import { useNavigate, useParams } from 'react-router-dom';
import StepThreeTestCasesPanel from '../components/StepThreeTestCasesPanel';
import { useAuth } from '../auth/AuthContext';
import { isLoadTestingProject } from '../utils/testingType';
import ProjectLlmChatWidget from '../components/ProjectLlmChatWidget';

export default function ProjectTestCasesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiClient } = useAuth();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qaRefresh, setQaRefresh] = useState(0);

  const loadProject = useCallback(async () => {
    if (!id) {
      setError('Не указан проект.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/projects/${id}`);
      setProject(response?.data || null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Не удалось загрузить проект.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, id]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const isLoadProject = isLoadTestingProject(project);

  return (
    <Stack spacing={3} className="stagger-children">
      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Typography variant="h4">
                {isLoadProject ? 'Шаг 2. Нагрузочные тест-кейсы' : 'Шаг 3. Test Cases (из шага 1)'}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Chip label={`Проект: ${project?.name || '—'}`} />
                <Chip label={`Требования: ${project?.requirementsCount || 0}`} />
              </Stack>
            </Stack>

            {isLoadProject ? (
              <Stack direction="row" justifyContent="flex-start" alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<WestIcon />}
                  onClick={() => navigate(`/projects/${id}`)}
                >
                  К шагу 1
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<WestIcon />}
                  onClick={() => navigate(`/projects/${id}/story-qa`)}
                >
                  К шагу 2
                </Button>
                <Button
                  variant="outlined"
                  endIcon={<EastIcon />}
                  onClick={() => navigate(`/projects/${id}/checklist`)}
                >
                  К шагу 4
                </Button>
              </Stack>
            )}
          </Stack>

          <Divider />

          <StepThreeTestCasesPanel
            projectId={id}
            apiClient={apiClient}
            refreshSignal={qaRefresh}
            mode={isLoadProject ? 'load' : 'manual'}
            disabled={(project?.requirementsCount || 0) === 0}
            disabledReason={isLoadProject
              ? 'В проекте пока нет требований шага 1 для генерации нагрузочных тест-кейсов.'
              : 'В проекте пока нет требований шага 1 для генерации тест-кейсов.'}
          />
        </>
      )}
      {project && <ProjectLlmChatWidget project={project} stage="test_cases" onQaArtifactChanged={() => setQaRefresh((value) => value + 1)} />}
    </Stack>
  );
}
